/**
 * Unified runtime brand configuration surface — merges the (currently
 * always-null) runtime manifest from runtimeBrandManifest.js with the
 * existing build-time brand config, field by field:
 *
 *   1. runtime manifest field, if present and valid
 *   2. existing build-time / resolver value — i.e. exactly what the app
 *      already reads today from companyBrand.js, featureFlags.js and
 *      brandResolver.js, completely unchanged
 *
 * Infrastructure only: not imported by any screen, and does not replace
 * any existing import of companyBrand.js / brand.js / featureFlags.js /
 * brandResolver.js — this is a new, parallel, additive surface for a
 * later phase to adopt when it actually wants manifest-driven overrides.
 *
 * Expected (optional) manifest shape, every field optional:
 *   {
 *     "brandId": "acme",
 *     "appName": "Acme App",
 *     "companyName": "Acme Corp",
 *     "shortName": "ACME",
 *     "colors": { "primary": "#112233", "secondary": "#445566" },
 *     "logoPath": "/acme-logo.png",
 *     "featureFlags": { "payroll": false }
 *   }
 *
 * getRuntimeBrandConfig() is a function (not a module-level constant) so
 * it always reflects the current manifest/env at call time, and so tests
 * don't need jest.resetModules() gymnastics to exercise different
 * scenarios.
 *
 * Two entry points, sharing the same field-merging logic:
 *   - getRuntimeBrandConfig()  — synchronous, unchanged signature/shape.
 *     Uses loadRuntimeBrandManifest() (window-only, tier 1). This is what
 *     BrandContext uses for its instant first-render snapshot, and is
 *     kept exactly as before for any other existing synchronous caller.
 *   - loadRuntimeBrandConfig() — async. Uses
 *     loadRuntimeBrandManifestAsync() (window, then a cached
 *     network fetch of the generated manifest — tier 2). Resolves to the
 *     exact same shape as getRuntimeBrandConfig(); never rejects.
 */
import {
  loadRuntimeBrandManifest,
  loadRuntimeBrandManifestAsync,
} from "./runtimeBrandManifest";
import { resolveBrandId } from "./brandResolver";
import brand from "./brand";
import {
  BRAND_NAME,
  BRAND_SHORT_NAME,
  BRAND_SLOGAN,
  BRAND_PRIMARY_COLOR,
  BRAND_SECONDARY_COLOR,
  BRAND_LOGO_PATH,
} from "./companyBrand";
import { DEFAULT_FEATURE_FLAGS, BRAND_FEATURE_FLAGS } from "./featureFlags";

function pickString(manifestValue, fallbackValue) {
  return typeof manifestValue === "string" && manifestValue.trim()
    ? manifestValue.trim()
    : fallbackValue;
}

function mergeFeatureFlags(manifestFlags) {
  const merged = { ...BRAND_FEATURE_FLAGS };
  if (!manifestFlags || typeof manifestFlags !== "object") {
    return merged;
  }
  Object.keys(DEFAULT_FEATURE_FLAGS).forEach((key) => {
    if (typeof manifestFlags[key] === "boolean") {
      merged[key] = manifestFlags[key];
    }
  });
  return merged;
}

/**
 * Shared field-by-field merge: manifest value (if present/valid) wins,
 * otherwise fall back to today's existing build-time/default value.
 * Pure function, no I/O — used by both getRuntimeBrandConfig() and
 * loadRuntimeBrandConfig() so the two never drift apart.
 */
function buildConfigFromManifest(manifest) {
  const safeManifest =
    manifest && typeof manifest === "object" ? manifest : {};
  const colors =
    safeManifest.colors && typeof safeManifest.colors === "object"
      ? safeManifest.colors
      : {};

  return {
    brandId: pickString(safeManifest.brandId, resolveBrandId()),
    appName: pickString(safeManifest.appName, brand.appName),
    companyName: pickString(safeManifest.companyName, BRAND_NAME),
    shortName: pickString(safeManifest.shortName, BRAND_SHORT_NAME),
    slogan: pickString(safeManifest.slogan, BRAND_SLOGAN),
    primaryColor: pickString(colors.primary, BRAND_PRIMARY_COLOR),
    secondaryColor: pickString(colors.secondary, BRAND_SECONDARY_COLOR),
    logoPath: pickString(safeManifest.logoPath, BRAND_LOGO_PATH),
    featureFlags: mergeFeatureFlags(safeManifest.featureFlags),
  };
}

/**
 * Returns { brandId, appName, companyName, shortName, slogan, primaryColor, secondaryColor,
 * logoPath, featureFlags }. Never throws — any error while reading the
 * manifest is swallowed and treated as "no manifest", falling all the way
 * through to today's existing values.
 *
 * Synchronous: only ever sees a manifest from window.__BRAND_MANIFEST__
 * (tier 1). Unchanged signature/behavior from before this phase — safe
 * for any existing caller (e.g. BrandContext's instant first-render
 * snapshot).
 */
export function getRuntimeBrandConfig() {
  let manifest = null;
  try {
    manifest = loadRuntimeBrandManifest();
  } catch {
    manifest = null;
  }

  return buildConfigFromManifest(manifest);
}

/**
 * Async counterpart of getRuntimeBrandConfig(): also tries fetching
 * public/brand-manifest.generated.json (tier 2) when no
 * window.__BRAND_MANIFEST__ is present. Resolves to the exact same shape
 * as getRuntimeBrandConfig(). Never rejects — any failure anywhere in the
 * chain (network, JSON, shape) resolves to the same build-time/ESS
 * fallback values as the synchronous version.
 */
export async function loadRuntimeBrandConfig() {
  let manifest = null;
  try {
    manifest = await loadRuntimeBrandManifestAsync();
  } catch {
    manifest = null;
  }

  return buildConfigFromManifest(manifest);
}

export default getRuntimeBrandConfig;
